/**
 * Liga a escuta do "Jarvis" e do "Atlas" no motor escolhido nesta máquina
 * (spec 011):
 *   - 'chrome': reconhecedor LOCAL do Chrome (`webSpeechWake`);
 *   - 'proprio': Vosk na página (`voskWake`, fase 5), com dois ouvidos no
 *     mesmo microfone (05/10/2026: até então o motor próprio não ouvia o
 *     "Atlas", e o Testar do notebook ficava mudo):
 *       · modelo inglês pequeno, sempre ouvindo, gramática "jarvis" + "atlas"
 *         (o pt-BR não tem "jarvis"). É o porteiro dos dois nomes;
 *       · modelo português pequeno, SOB DEMANDA: só acorda quando o inglês
 *         ouve "atlas", e começa pelo áudio guardado desde o nome — entende o
 *         pedido ("manda entrar", "imprime os óculos"…). Os dois sempre
 *         ligados custavam o dobro de CPU (medido no notebook: ~28% → ~53%
 *         de um núcleo); assim o português só gasta enquanto o pedido é dito.
 *
 * Mesma interface pros dois motores: quem chama (`WakeWordWatcher`, o Testar
 * da configuração) não sabe qual está rodando e passa o texto pelo matcher
 * de sempre. No motor próprio o "atlas" chega sozinho e o pedido vem na
 * frase seguinte — o mesmo caminho do "Atlas… manda entrar" com pausa no
 * Chrome (o bipe curto toca sempre). Do Vosk só sai o que conta (confiança
 * suficiente); o resto do que ele ouve morre aqui.
 */
import type { WakeEngine } from './store';
import { RESPOSTAS_CONFERENCIA_VOSK } from './resposta';
import { voskGrammar, voskTexto } from './voskDetect';
import { startVoskWakeListener, type VoskWakeHandle } from './voskWake';
import { startWakeListener, type WakeListenerOptions } from './webSpeechWake';

/** Publicados pelo `scripts/vosk-assets.mjs` (roda antes do build). */
export const VOSK_BASE_URL = '/vosk/0.0.8';
export const VOSK_MODELO = 'vosk-model-small-en-us-0.15';
export const VOSK_MODELO_ATLAS = 'vosk-model-small-pt-0.3';
/** Tamanho do download, pra avisar na configuração (vosk.js + os dois modelos). */
export const VOSK_DOWNLOAD_MB = 79;
/** Abaixo disso a palavra não conta (no teste de 30/09 a voz real deu 1,00 no "jarvis"). */
export const VOSK_MIN_CONF = 0.7;
/** O português começa um pouco antes do "atlas" que o inglês ouviu. */
const ATLAS_MARGEM_S = 0.5;
/**
 * Quanto o português fica acordado depois do "atlas": a espera do pedido no
 * `WakeWordWatcher` (8 s desde 05/10/2026, era 5) mais o tempo de ele fechar
 * a frase.
 */
export const ATLAS_ESCUTA_MS = 9500;

/**
 * Isca do português: com gramática o Vosk sempre encaixa o som em alguma
 * palavra dela, e "Jarvis" dito com ele acordado podia virar pedido. A isca
 * atrai o som parecido e é descartada (o "atlas" também: quem anuncia o nome
 * é o inglês).
 */
const ISCA_PT = ['jarbas'];

/**
 * O que o ouvido português pode entender (fora isso, `[unk]`): os pedidos do
 * `matcher.ts`, nas formas que ele aceita, com acento (é assim que estão no
 * vocabulário do modelo). Pedido dito de outro jeito não passa: sinônimo
 * novo entra AQUI e no matcher (o teste confere os dois juntos).
 * O Vosk aceita qualquer sequência destes itens, então o que imprimir vem em
 * pedaços: "imprime" + "os óculos" + "os exames". Muleta e "e" ficam de fora
 * de propósito: viram `[unk]` e somem (um "é…" sozinho depois do "Atlas"
 * chegaria como frase e gastaria a espera do pedido).
 */
export const PEDIDOS_ATLAS_VOSK = [
  // proximo
  'abre o próximo',
  'abre a próxima',
  'abra o próximo',
  'abre a ficha do próximo',
  'abre a ficha da próxima',
  // mandarEntrar
  'manda entrar',
  'mande entrar',
  'pode mandar entrar',
  'manda o paciente entrar',
  'manda o próximo entrar',
  'manda o próximo paciente entrar',
  'manda ele entrar',
  'manda ela entrar',
  'chama o próximo',
  'chama a próxima',
  'chama o paciente',
  // agenda
  'abre a agenda',
  'volta pra agenda',
  'volta para a agenda',
  'vai pra agenda',
  'mostra a agenda',
  // imprimir
  'imprime',
  'imprima',
  'pode imprimir',
  'tudo',
  'os documentos',
  'os óculos',
  'a receita',
  'os exames',
  'a cirurgia',
  // salvar (05/10/2026)
  'salve',
  'salva',
  'salvar',
  'pode salvar',
  'salve no prontuário',
  'salva no prontuário',
  'registra',
  'registre',
  'registra no prontuário',
  // pausar (05/10/2026)
  'pause',
  'pausa',
  'pausar',
  'pausa o ditado',
  // retomar (05/10/2026)
  'continua',
  'continue',
  'retoma',
  'retome',
  'volta a ouvir',
  // parar
  'desliga',
  'desliga o ditado',
  'desliga o ao vivo',
  'para o ditado',
  'pare o ditado',
  'encerra o ditado',
  'para de ouvir',
  'para o ao vivo',
  'pare o ao vivo',
  'parar ao vivo',
  'para',
  'pare',
  'parar',
];

/** Quanto tempo a tecla do Atlas insiste em acordar o português (áudio começando, modelo carregando). */
const ATLAS_TECLA_TENTA_MS = 5000;
const ATLAS_TECLA_PASSO_MS = 200;

/** O "desliga" da escuta, mais o "Atlas" pela tecla do controle. */
export interface WakeListenerHandle {
  (): void;
  /**
   * Tecla do Atlas (22/10/2026): o pedido vem sem o nome. No motor próprio
   * acorda o ouvido português a partir de agora (o inglês só acorda ao ouvir
   * "atlas"); no do Chrome não precisa — ele já transcreve tudo.
   */
  esperarPedido: () => void;
}

export function startEngineWakeListener(motor: WakeEngine, o: WakeListenerOptions): WakeListenerHandle {
  if (motor === 'chrome') return Object.assign(startWakeListener(o), { esperarPedido: () => {} });
  let h: VoskWakeHandle | null = null;
  let tentativa: ReturnType<typeof setTimeout> | null = null;
  h = startVoskWakeListener({
    baseUrl: VOSK_BASE_URL,
    ouvidos: [
      {
        modelo: VOSK_MODELO,
        gramatica: voskGrammar(['jarvis', 'atlas']),
        onFinal: (res) => {
          for (const w of res.result ?? []) {
            if (w.conf < VOSK_MIN_CONF) continue;
            if (w.word === 'jarvis') o.onFinal('jarvis');
            if (w.word !== 'atlas') continue;
            // Acorda o português no começo do nome: ele ouve o pedido colado
            // ("Atlas, manda entrar") e o que vier na pausa depois.
            h?.acordar(VOSK_MODELO_ATLAS, (w.start ?? w.end - 1) - ATLAS_MARGEM_S, ATLAS_ESCUTA_MS);
            o.onFinal('atlas');
          }
        },
      },
      {
        modelo: VOSK_MODELO_ATLAS,
        // O nome entra na gramática pra não ser encaixado num pedido. As
        // respostas da conferência (spec 023, fase 3) também: o mesmo ouvido
        // acorda pra janela do "Imprimo?" (`esperarPedido`).
        gramatica: voskGrammar([...new Set(['atlas', ...PEDIDOS_ATLAS_VOSK, ...RESPOSTAS_CONFERENCIA_VOSK, ...ISCA_PT])]),
        sobDemanda: true,
        // Sem o português o "Jarvis" continua funcionando.
        opcional: true,
        onFinal: (res) => {
          const texto = voskTexto(res, VOSK_MIN_CONF, ['atlas', ...ISCA_PT]);
          if (texto) o.onFinal(texto);
        },
      },
    ],
    onState: (e, d) => o.onState(e === 'carregando' ? 'verificando' : e, d),
  });
  const vosk = h;
  const esperarPedido = () => {
    if (tentativa) clearTimeout(tentativa);
    const apertou = performance.now();
    const ate = Date.now() + ATLAS_TECLA_TENTA_MS;
    const tentar = () => {
      tentativa = null;
      // Áudio ainda não começou (`inicio` 0): ouve desde o começo dele.
      const comeco = vosk.inicio();
      const desdeS = comeco > 0 ? Math.max(0, (apertou - comeco) / 1000 - ATLAS_MARGEM_S) : 0;
      if (vosk.acordar(VOSK_MODELO_ATLAS, desdeS, ATLAS_ESCUTA_MS)) return;
      if (Date.now() < ate) tentativa = setTimeout(tentar, ATLAS_TECLA_PASSO_MS);
    };
    tentar();
  };
  return Object.assign(
    () => {
      if (tentativa) clearTimeout(tentativa);
      tentativa = null;
      vosk.stop();
    },
    { esperarPedido },
  );
}
